import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import useGetHook from "../../../customHooks/useGetHook";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useDebounce } from "../../../customHooks/useDebounceHook";
export default function AccountComboboxInput(props: any) {
  const {
    url,
    queryKey,
    props: otherProps,
    errors,
    setValue,
    name,
    getValues,
    clearErrors,
    disabled = false,
    focus = false,
  } = props;
  const limit: number = 10;
  const [query, setQuery] = useState("");
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const [selected, setSelected] = useState<any>(null);
  const debouncesearch = useDebounce(query);
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const comboboxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(name, selected?.id ?? null);
  }, [selected]);

  useEffect(() => {
    const currentValue = getValues(name);
    if (!selected && currentValue) {
      setSelected(currentValue);
    } else if (typeof currentValue === "object" && currentValue !== null) {
      setValue(name, selected?.id ?? null);
    } else if (!currentValue) {
      setSelected(null);
    }
  }, [getValues(name)]);

  const filteredData: any =
    query === ""
      ? data
      : data.filter(
          (item: any) =>
            item?.name ||
            item?.account_full_name
              .toLowerCase()
              .replace(/\s+/g, "")
              .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  const { data: serverData } = useGetHook({
    queryKey: [queryKey, `${queryKey}-${limit}-${debouncesearch}`],
    url: url,
    params: {
      limit: limit,
      offset: 0,
      search: debouncesearch,
      ...otherProps,
    },
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData(serverData?.results ?? []);
    }
  }, [serverData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleFocusOutside = () => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(document.activeElement as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("focusin", handleFocusOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleFocusOutside);
    };
  }, [comboboxRef]);

  return (
    <>
      <Combobox
        value={selected}
        disabled={disabled}
        onChange={(value) => {
          setSelected(value);
          setIsOpen(false); // Close combobox on selection
        }}
      >
        <div ref={comboboxRef} className={tw`relative `}>
          <label>{props?.label}</label>
          <div
            className={tw`relative w-full cursor-default overflow-hidden bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm mt-2`}
          >
            <Combobox.Input
              className={tw`w-full focus:outline-none bg-[#EEF5F4] p-[8px] text-left`}
              placeholder={props?.placeholder}
              autoComplete="off"
              onBlur={() => clearErrors(name)}
              displayValue={(item: any) => item?.account_full_name}
              onChange={(event) => {
                setQuery(event.target.value);
                setIsOpen(true);
              }}
              onClick={() => setIsOpen(true)}
              // onFocus={() => setIsOpen(true)}
              autoFocus={focus}
            />
            <Combobox.Button
              ref={comboboxButtonRef}
              className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
              onClick={() => setIsOpen(!isOpen)}
            >
              {selected && (
                <PlusIcon
                  className={tw`h-5 w-5 text-gray-400 rotate-45`}
                  aria-hidden="true"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent Combobox from opening
                    setSelected(null); // Clear selection
                    setValue(name, ""); // Reset form value
                  }}
                />
              )}
              <ChevronDownIcon
                className={tw`h-5 w-5 text-gray-400`}
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            show={isOpen}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options
              static
              className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
            >
              {filteredData?.length === 0 && query !== "" ? (
                <div
                  className={tw`relative cursor-default select-none py-2 px-4 text-gray-700`}
                >
                  Nothing found.
                </div>
              ) : (
                filteredData.map((item: any) => (
                  <Combobox.Option
                    key={item.id}
                    className={({ active }) =>
                      tw`relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? tw`bg-primary text-white` : tw`text-gray-900`
                      }`
                    }
                    value={item}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={tw`block truncate ${
                            selected ? tw`font-medium` : tw`font-normal`
                          }`}
                        >
                          {item?.account_full_name}
                        </span>
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
          {errors && (
            <p className={tw`text-red-500 text-xs my-1`}>{errors?.message}</p>
          )}
        </div>
      </Combobox>
    </>
  );
}
